import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Mail } from "lucide-react";
import {
  fetchDailyTips,
  fetchDailySales,
  fetchEmployees,
  fetchPayPeriods,
  fetchEvents,
  fetchKitchenTipouts,
  fetchCoachDailyTips,
  fetchCoachEvents,
  fetchCoachEventAssignments,
  fetchSquareTipShare,
  fetchSquareTipShareEntries,
} from "../tipsApi";
import { apiSend } from "../../../lib/api";
import { buildReportRows, sumReportRows } from "../reportAggregate";
import { ROLE_META, ROLE_ORDER } from "../roleMeta";
import type { ReportRow } from "../reportAggregate";
import type { DailyTip, DailySales, Employee, EmployeeRole, PayPeriod, TipEvent, KitchenTipout, CoachDailyTip, CoachEventAssignment, SquareTipShare, SquareTipShareEntry } from "../types";

type PeriodKey = "pay-period" | "this-month" | "last-30" | "this-quarter" | "this-year" | "custom";
type RoleFilter = "all" | EmployeeRole;

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "pay-period", label: "Pay Period" },
  { key: "this-month", label: "This Month" },
  { key: "last-30", label: "Last 30 Days" },
  { key: "this-quarter", label: "This Quarter" },
  { key: "this-year", label: "This Year" },
  { key: "custom", label: "Custom" },
];

const CARD_COLS: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
  5: "sm:grid-cols-5",
};

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function periodRange(key: PeriodKey, payPeriods: PayPeriod[], customFrom: string, customTo: string): [string, string] {
  const now = new Date();
  switch (key) {
    case "pay-period": {
      const pp = payPeriods[0];
      return pp ? [pp.start_date, pp.end_date] : [iso(now), iso(now)];
    }
    case "this-month": {
      const y = now.getFullYear();
      const m = now.getMonth();
      return [iso(new Date(y, m, 1)), iso(now)];
    }
    case "last-30": {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return [iso(d), iso(now)];
    }
    case "this-quarter": {
      const y = now.getFullYear();
      const q = Math.floor(now.getMonth() / 3) * 3;
      return [iso(new Date(y, q, 1)), iso(now)];
    }
    case "this-year":
      return [`${now.getFullYear()}-01-01`, iso(now)];
    case "custom":
      return [customFrom || iso(now), customTo || iso(now)];
  }
}

function money(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function roleLabels(row: ReportRow): string {
  const ordered = ROLE_ORDER.filter((r) => row.roles.includes(r));
  return (ordered.length > 0 ? ordered : [row.primaryRole]).map((r) => ROLE_META[r].label).join(" / ");
}

export function TipsReportsView() {
  const [periodKey, setPeriodKey] = useState<PeriodKey>("pay-period");
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  const [selectedPpId, setSelectedPpId] = useState<string | null>(null);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [tips, setTips] = useState<DailyTip[]>([]);
  const [sales, setSales] = useState<DailySales[]>([]);
  const [barEvents, setBarEvents] = useState<TipEvent[]>([]);
  const [kitchenTipouts, setKitchenTipouts] = useState<KitchenTipout[]>([]);
  const [coachTips, setCoachTips] = useState<CoachDailyTip[]>([]);
  const [coachAssignments, setCoachAssignments] = useState<CoachEventAssignment[]>([]);
  const [squareShares, setSquareShares] = useState<SquareTipShare[]>([]);
  const [squareEntries, setSquareEntries] = useState<SquareTipShareEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailTo, setEmailTo] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);

  const loadBase = useCallback(async () => {
    const [pp, emp] = await Promise.all([
      fetchPayPeriods(),
      fetchEmployees(),
    ]);
    setPayPeriods(pp);
    setEmployees(emp);
    if (pp.length > 0) setSelectedPpId(pp[0].id);
    setLoading(false);
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);

  const selectedPp = useMemo(
    () => payPeriods.find((p) => p.id === selectedPpId) ?? payPeriods[0] ?? null,
    [payPeriods, selectedPpId],
  );

  const [from, to] = useMemo(() => {
    if (periodKey === "pay-period" && selectedPp) return [selectedPp.start_date, selectedPp.end_date];
    return periodRange(periodKey, payPeriods, customFrom, customTo);
  }, [periodKey, payPeriods, selectedPp, customFrom, customTo]);

  const overlappingPeriods = useMemo(() => {
    if (!from || !to) return [];
    return payPeriods.filter((pp) => pp.start_date <= to && pp.end_date >= from);
  }, [payPeriods, from, to]);

  const loadData = useCallback(async () => {
    if (!from || !to) return;
    const [t, s, ev, kt, ct, ce] = await Promise.all([
      fetchDailyTips(from, to),
      fetchDailySales(from, to),
      fetchEvents(from, to),
      fetchKitchenTipouts(from, to),
      fetchCoachDailyTips(from, to),
      fetchCoachEvents(from, to),
    ]);
    setTips(t);
    setSales(s);
    setBarEvents(ev);
    setKitchenTipouts(kt);
    setCoachTips(ct);
    if (ce.length > 0) {
      setCoachAssignments(await fetchCoachEventAssignments(ce.map((e) => e.id)));
    } else {
      setCoachAssignments([]);
    }

    // The Square sheet is keyed per pay period, so a month/quarter/year range
    // has to gather every period it touches or the payouts come up short.
    const perPeriod = await Promise.all(
      overlappingPeriods.map(async (pp) => {
        const [share, entries] = await Promise.all([
          fetchSquareTipShare(pp.id),
          fetchSquareTipShareEntries(pp.id),
        ]);
        return { share, entries };
      }),
    );
    setSquareShares(perPeriod.map((r) => r.share).filter((s): s is SquareTipShare => s !== null));
    setSquareEntries(perPeriod.flatMap((r) => r.entries));
  }, [from, to, overlappingPeriods]);

  useEffect(() => { loadData(); }, [loadData]);

  const [trendSales, setTrendSales] = useState<DailySales[]>([]);
  const [trendTips, setTrendTips] = useState<DailyTip[]>([]);

  const [trendFrom, trendTo] = useMemo(() => {
    if (!from || !to) return [from, to];
    const d0 = new Date(from + "T00:00:00");
    const d1 = new Date(to + "T00:00:00");
    const monthStart = iso(new Date(d0.getFullYear(), d0.getMonth(), 1));
    const monthEnd = iso(new Date(d1.getFullYear(), d1.getMonth() + 1, 0));
    return [monthStart, monthEnd];
  }, [from, to]);

  useEffect(() => {
    if (!trendFrom || !trendTo) return;
    if (trendFrom === from && trendTo === to) {
      setTrendSales(sales);
      setTrendTips(tips);
      return;
    }
    let cancelled = false;
    (async () => {
      const [ts, tt] = await Promise.all([
        fetchDailySales(trendFrom, trendTo),
        fetchDailyTips(trendFrom, trendTo),
      ]);
      if (!cancelled) {
        setTrendSales(ts);
        setTrendTips(tt);
      }
    })();
    return () => { cancelled = true; };
  }, [trendFrom, trendTo, from, to, sales, tips]);

  const allRows = useMemo(
    () => buildReportRows({ employees, tips, barEvents, kitchenTipouts, squareEntries, coachTips, coachAssignments }),
    [employees, tips, barEvents, kitchenTipouts, squareEntries, coachTips, coachAssignments],
  );

  const rows = useMemo(
    () => (roleFilter === "all" ? allRows : allRows.filter((r) => r.roles.includes(roleFilter))),
    [allRows, roleFilter],
  );

  const totals = useMemo(() => sumReportRows(rows), [rows]);
  const grandTotals = useMemo(() => sumReportRows(allRows), [allRows]);

  const roleCounts = useMemo(() => {
    const m = new Map<EmployeeRole, number>();
    for (const r of allRows) {
      for (const role of r.roles) m.set(role, (m.get(role) ?? 0) + 1);
    }
    return m;
  }, [allRows]);

  const totalSales = sales.reduce((s, d) => s + Number(d.total_sales), 0);
  const totalRawTips = tips.reduce((s, t) => s + Number(t.raw_tips), 0);
  const totalEventNet = barEvents.reduce((s, ev) => s + Number(ev.net_to_pool), 0);
  const tipPct = totalSales > 0 ? (totalRawTips / totalSales) * 100 : 0;
  const avgHourly = totals.hours > 0 ? totals.total / totals.hours : 0;

  const squarePool = useMemo(() => {
    const totalTips = squareShares.reduce((s, sh) => s + Number(sh.total_tips), 0);
    const fee = squareShares.reduce(
      (s, sh) => s + Number(sh.total_tips) * (Number(sh.processing_fee_pct) / 100),
      0,
    );
    return {
      totalTips: Math.round(totalTips * 100) / 100,
      fee: Math.round(fee * 100) / 100,
      netPool: Math.round((totalTips - fee) * 100) / 100,
    };
  }, [squareShares]);

  const monthlyTrend = useMemo(() => {
    const m = new Map<string, { sales: number; tips: number }>();
    for (const s of trendSales) {
      const mo = s.date.slice(0, 7);
      const entry = m.get(mo) ?? { sales: 0, tips: 0 };
      entry.sales += Number(s.total_sales);
      m.set(mo, entry);
    }
    for (const t of trendTips) {
      const mo = t.date.slice(0, 7);
      const entry = m.get(mo) ?? { sales: 0, tips: 0 };
      entry.tips += Number(t.raw_tips);
      m.set(mo, entry);
    }
    return Array.from(m.entries())
      .map(([month, data]) => ({
        month,
        label: new Date(month + "-01T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }),
        ...data,
        pct: data.sales > 0 ? (data.tips / data.sales) * 100 : 0,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [trendSales, trendTips]);

  const showExtra = totals.extraTips > 0;
  const periodLabel = periodKey === "pay-period" && selectedPp
    ? `${selectedPp.start_date} to ${selectedPp.end_date}`
    : `${from} to ${to}`;

  const summaryCards = [
    { label: "Total Sales", value: money(totalSales), subtitle: `Bar tip %: ${tipPct.toFixed(1)}%` },
    { label: "Total Tips", value: money(totals.total), subtitle: roleFilter === "all" ? "All roles" : ROLE_META[roleFilter].label },
    ...(totalEventNet > 0 ? [{ label: "Event Gratuities", value: money(totalEventNet), subtitle: "Net to bar pool" }] : []),
    ...(squarePool.totalTips > 0 ? [{ label: "Square Tip Pool", value: money(squarePool.netPool), subtitle: `After ${money(squarePool.fee)} fee` }] : []),
    { label: "Avg $/hr", value: money(avgHourly), subtitle: "Hours-tracked roles" },
  ];

  function exportCSV() {
    let csv = "Role,Employee,Hours,Tips,Extra Tips,Event Tips,Total,$/hr\n";
    for (const r of rows) {
      const rate = r.hours > 0 ? (r.total / r.hours).toFixed(2) : "";
      csv += `"${roleLabels(r)}","${r.name}",${r.hours.toFixed(2)},${r.tips.toFixed(2)},${r.extraTips.toFixed(2)},${r.eventTips.toFixed(2)},${r.total.toFixed(2)},${rate}\n`;
    }
    csv += `"Total",,${totals.hours.toFixed(2)},${totals.tips.toFixed(2)},${totals.extraTips.toFixed(2)},${totals.eventTips.toFixed(2)},${totals.total.toFixed(2)},${totals.hours > 0 ? (totals.total / totals.hours).toFixed(2) : ""}\n`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tips-report-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function buildEmailHtml(): string {
    const th = (align: "left" | "right") => `text-align: ${align}; padding: 8px;`;
    const td = (align: "left" | "right") => `padding: 6px 8px; text-align: ${align};`;

    let html = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 760px; margin: 0 auto;">`;
    html += `<h2 style="color: #1a1a1a; border-bottom: 2px solid #c41e3a; padding-bottom: 8px;">Tip Report: ${periodLabel}</h2>`;
    html += `<p style="font-size: 13px; color: #666;">All roles combined — bartenders, axe coaches, front desk and kitchen.`;
    if (squarePool.totalTips > 0) {
      html += ` Square pool: ${money(squarePool.totalTips)} · Fee: −${money(squarePool.fee)} · Net: ${money(squarePool.netPool)}.`;
    }
    html += `</p>`;

    if (rows.length > 0) {
      html += `<table style="width: 100%; border-collapse: collapse; font-size: 14px;">`;
      html += `<thead><tr style="background: #f5f5f5; border-bottom: 2px solid #ddd;">`;
      html += `<th style="${th("left")}">Role</th>`;
      html += `<th style="${th("left")}">Employee</th>`;
      html += `<th style="${th("right")}">Hours</th>`;
      html += `<th style="${th("right")}">Tips</th>`;
      if (showExtra) html += `<th style="${th("right")}">Extra</th>`;
      html += `<th style="${th("right")}">Event Tips</th>`;
      html += `<th style="${th("right")}">Total</th>`;
      html += `<th style="${th("right")}">$/hr</th>`;
      html += `</tr></thead><tbody>`;
      for (const r of rows) {
        html += `<tr style="border-bottom: 1px solid #eee;">`;
        html += `<td style="${td("left")} color: #666;">${roleLabels(r)}</td>`;
        html += `<td style="${td("left")} font-weight: 500;">${r.name}</td>`;
        html += `<td style="${td("right")}">${r.hours > 0 ? r.hours.toFixed(2) : "—"}</td>`;
        html += `<td style="${td("right")}">${money(r.tips)}</td>`;
        if (showExtra) html += `<td style="${td("right")}">${r.extraTips > 0 ? money(r.extraTips) : "—"}</td>`;
        html += `<td style="${td("right")} color: #b8860b;">${r.eventTips > 0 ? money(r.eventTips) : "—"}</td>`;
        html += `<td style="${td("right")} font-weight: 600;">${money(r.total)}</td>`;
        html += `<td style="${td("right")} color: #666;">${r.hours > 0 ? money(r.total / r.hours) : "—"}</td>`;
        html += `</tr>`;
      }
      html += `<tr style="background: #f9f9f9; font-weight: 600; border-top: 2px solid #ddd;">`;
      html += `<td style="${th("left")}" colspan="2">Total</td>`;
      html += `<td style="${th("right")}">${totals.hours.toFixed(2)}</td>`;
      html += `<td style="${th("right")}">${money(totals.tips)}</td>`;
      if (showExtra) html += `<td style="${th("right")}">${money(totals.extraTips)}</td>`;
      html += `<td style="${th("right")} color: #b8860b;">${money(totals.eventTips)}</td>`;
      html += `<td style="${th("right")}">${money(totals.total)}</td>`;
      html += `<td style="${th("right")} color: #666;">${totals.hours > 0 ? money(totals.total / totals.hours) : "—"}</td>`;
      html += `</tr></tbody></table>`;
    } else {
      html += `<p style="font-size: 14px;">No tip data for this period.</p>`;
    }

    html += `<p style="margin-top: 24px; font-size: 12px; color: #999;">Generated from Tribal Axe HQ · ${new Date().toLocaleString()}</p>`;
    html += `</div>`;
    return html;
  }

  async function handleSendEmail() {
    if (!emailTo.trim()) return;
    setSending(true);
    setEmailStatus(null);
    try {
      await apiSend("/members/send-email", "POST", {
        to: [emailTo.trim()],
        subject: `Tribal Axe — Tip Report: ${periodLabel}`,
        html: buildEmailHtml(),
      });
      setEmailStatus("sent");
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailStatus(null);
      }, 2000);
    } catch (e) {
      setEmailStatus(`Error: ${(e as Error).message}`);
    } finally {
      setSending(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-tribal-muted">Loading…</div>;

  return (
    <div className="mx-auto max-w-5xl p-6">
      {/* Period selector */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriodKey(p.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                periodKey === p.key
                  ? "bg-tribal-red/20 text-white"
                  : "bg-white/5 text-tribal-muted hover:bg-white/10"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {periodKey === "pay-period" && payPeriods.length > 0 && (
          <select
            value={selectedPpId ?? ""}
            onChange={(e) => setSelectedPpId(e.target.value)}
            className="rounded-md border border-tribal-border bg-tribal-bg px-2 py-1 text-xs"
          >
            {payPeriods.map((pp) => (
              <option key={pp.id} value={pp.id}>
                {pp.start_date} — {pp.end_date}
              </option>
            ))}
          </select>
        )}
        {periodKey === "custom" && (
          <div className="flex items-center gap-2 text-xs">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded border border-tribal-border bg-tribal-bg px-2 py-1" />
            <span className="text-tribal-muted">to</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
              className="rounded border border-tribal-border bg-tribal-bg px-2 py-1" />
          </div>
        )}
      </div>

      {/* Role filter */}
      <div className="mb-6 flex flex-wrap items-center gap-1">
        <RoleChip
          label="All roles"
          count={allRows.length}
          active={roleFilter === "all"}
          onClick={() => setRoleFilter("all")}
        />
        {ROLE_ORDER.map((role) => (
          <RoleChip
            key={role}
            label={ROLE_META[role].label}
            count={roleCounts.get(role) ?? 0}
            active={roleFilter === role}
            onClick={() => setRoleFilter(role)}
          />
        ))}
      </div>

      {/* Summary cards */}
      <div className={`mb-6 grid grid-cols-2 gap-3 ${CARD_COLS[summaryCards.length] ?? "sm:grid-cols-4"}`}>
        {summaryCards.map((c) => (
          <SummaryCard key={c.label} label={c.label} value={c.value} subtitle={c.subtitle} />
        ))}
      </div>

      {/* Action buttons */}
      <div className="mb-6 flex items-center gap-2">
        <button
          type="button"
          onClick={exportCSV}
          className="flex items-center gap-1 rounded-md bg-white/5 px-2.5 py-1 text-xs text-tribal-muted hover:bg-white/10 hover:text-white"
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
        <button
          type="button"
          onClick={() => setShowEmailModal(true)}
          className="flex items-center gap-1 rounded-md bg-tribal-red/20 px-2.5 py-1 text-xs text-white hover:bg-tribal-red/30"
        >
          <Mail className="h-3.5 w-3.5" /> Email Report
        </button>
      </div>

      {/* Combined per-employee payout table */}
      {rows.length > 0 && (
        <section className="mb-6">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold">Tips by Employee</h3>
            {squarePool.totalTips > 0 && (
              <div className="flex gap-3 text-xs text-tribal-muted">
                <span>Square pool: <span className="tabular-nums text-white">{money(squarePool.totalTips)}</span></span>
                <span>Fee: <span className="tabular-nums text-red-400">−{money(squarePool.fee)}</span></span>
                <span>Net: <span className="font-medium tabular-nums text-white">{money(squarePool.netPool)}</span></span>
              </div>
            )}
          </div>
          <div className="overflow-x-auto rounded-lg border border-tribal-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-tribal-border bg-tribal-surface text-left text-xs text-tribal-muted">
                  <th className="px-4 py-2 font-medium">Role</th>
                  <th className="px-4 py-2 font-medium">Employee</th>
                  <th className="px-4 py-2 text-right font-medium">Hours</th>
                  <th className="px-4 py-2 text-right font-medium">Tips</th>
                  {showExtra && <th className="px-4 py-2 text-right font-medium">Extra</th>}
                  <th className="px-4 py-2 text-right font-medium">Event Tips</th>
                  <th className="px-4 py-2 text-right font-medium">Total</th>
                  <th className="px-4 py-2 text-right font-medium">$/hr</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-tribal-border last:border-0 hover:bg-white/[0.02]">
                    <td className="px-4 py-2">
                      <span className="flex flex-wrap gap-1">
                        {ROLE_ORDER.filter((role) => r.roles.includes(role)).map((role) => (
                          <span key={role} className={`rounded-full px-2 py-0.5 text-[10px] ${ROLE_META[role].badgeCls}`}>
                            {ROLE_META[role].label}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-medium">{r.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.hours > 0 ? r.hours.toFixed(2) : "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.tips)}</td>
                    {showExtra && (
                      <td className="px-4 py-2 text-right tabular-nums">{r.extraTips > 0 ? money(r.extraTips) : "—"}</td>
                    )}
                    <td className="px-4 py-2 text-right tabular-nums text-amber-300">
                      {r.eventTips > 0 ? money(r.eventTips) : "—"}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{money(r.total)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-tribal-muted">
                      {r.hours > 0 ? money(r.total / r.hours) : "—"}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-tribal-border bg-tribal-surface font-medium">
                  <td className="px-4 py-2" colSpan={2}>Total</td>
                  <td className="px-4 py-2 text-right tabular-nums">{totals.hours.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(totals.tips)}</td>
                  {showExtra && <td className="px-4 py-2 text-right tabular-nums">{money(totals.extraTips)}</td>}
                  <td className="px-4 py-2 text-right tabular-nums text-amber-300">{money(totals.eventTips)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(totals.total)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-tribal-muted">
                    {totals.hours > 0 ? money(totals.total / totals.hours) : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {roleFilter !== "all" && (
            <p className="mt-2 text-xs text-tribal-muted">
              Showing {ROLE_META[roleFilter].label} only — {money(totals.total)} of {money(grandTotals.total)} for the period.
            </p>
          )}
        </section>
      )}

      {rows.length === 0 && (
        <p className="mb-6 text-sm text-tribal-muted">
          {allRows.length === 0
            ? "No tip data for the selected period."
            : `No ${ROLE_META[roleFilter as EmployeeRole].label} tips for the selected period.`}
        </p>
      )}

      {/* Monthly trend */}
      {monthlyTrend.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold">Monthly Trend</h3>
          <div className="overflow-x-auto rounded-lg border border-tribal-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-tribal-border bg-tribal-surface text-left text-xs text-tribal-muted">
                  <th className="px-4 py-2 font-medium">Month</th>
                  <th className="px-4 py-2 text-right font-medium">Sales</th>
                  <th className="px-4 py-2 text-right font-medium">Tips</th>
                  <th className="px-4 py-2 text-right font-medium">Tip %</th>
                </tr>
              </thead>
              <tbody>
                {monthlyTrend.map((m) => (
                  <tr key={m.month} className="border-b border-tribal-border last:border-0 hover:bg-white/[0.02]">
                    <td className="px-4 py-2">{m.label}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(m.sales)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(m.tips)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{m.pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Email modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl border border-tribal-border bg-tribal-bg p-6 shadow-2xl">
            <h3 className="mb-4 text-lg font-semibold">Email Tip Report</h3>
            <p className="mb-3 text-xs text-tribal-muted">
              Sends the combined report for {periodLabel} — bartenders, axe coaches, front desk and kitchen in one table
              {roleFilter !== "all" && `, filtered to ${ROLE_META[roleFilter].label}`}.
            </p>
            <label className="mb-1 block text-xs text-tribal-muted">Recipient Email</label>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="manager@tribalaxe.com"
              className="mb-4 w-full rounded-md border border-tribal-border bg-tribal-surface px-3 py-2 text-sm focus:border-tribal-red focus:outline-none"
              onKeyDown={(e) => { if (e.key === "Enter") handleSendEmail(); }}
            />
            {emailStatus && (
              <div className={`mb-3 rounded-md px-3 py-2 text-xs ${
                emailStatus === "sent"
                  ? "bg-emerald-900/20 text-emerald-400"
                  : "bg-red-900/20 text-red-400"
              }`}>
                {emailStatus === "sent" ? "Report sent successfully!" : emailStatus}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowEmailModal(false); setEmailStatus(null); }}
                className="rounded-md bg-white/5 px-4 py-1.5 text-sm text-tribal-muted hover:bg-white/10 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendEmail}
                disabled={sending || !emailTo.trim()}
                className="rounded-md bg-tribal-red px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80 disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send Report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleChip({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        active ? "bg-white/15 text-white" : "bg-white/5 text-tribal-muted hover:bg-white/10"
      }`}
    >
      {label}
      <span className="ml-1.5 tabular-nums opacity-60">{count}</span>
    </button>
  );
}

function SummaryCard({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
  return (
    <div className="rounded-lg border border-tribal-border bg-tribal-surface px-4 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-tribal-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
      {subtitle && <div className="mt-0.5 text-[10px] text-tribal-muted">{subtitle}</div>}
    </div>
  );
}
